import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { BiCalendar } from '@react-icons/all-files/bi/BiCalendar';
import { BiMobile } from '@react-icons/all-files/bi/BiMobile';
import { BsArrowLeft } from '@react-icons/all-files/bs/BsArrowLeft';
import { HiOutlineDesktopComputer } from '@react-icons/all-files/hi/HiOutlineDesktopComputer';
import format from 'date-fns/format';
import getMinutes from 'date-fns/getMinutes';
import set from 'date-fns/set';
import { DatePicker, Select } from '@bucketplace/commerce-admin-ui';

import { useEditor } from '../../hooks';
import { TIME_OPTIONS } from '../Duration/DurationInput';
import { FieldItem } from '../FieldItem';

type DatePickerType = Date | null;
export const VIEWPORT_ID = 'canvas-viewport';

export function PreviewModeContainer({
  children,
}: {
  children: React.ReactElement[],
}) {
  const { state, setState } = useEditor();
  const viewPortRef = useRef<HTMLElement | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [width, setWidth] = useState<string | null>(null);
  // 선택 가능한 시간대는 10분 단위이므로 현재의 hh:mm과 일치하지 않을 수 있어서 00:00으로 초기화
  const displayDate = state?.displayDate ?? set(new Date(), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const handleClickClosePreview = () => {
    setState((state) => ({
      ...state,
      displayMode: 'edit',
    }));
  };

  const handleChangeDisplayDate = (value: [DatePickerType, DatePickerType]): void => {
    setState((state) => ({
      ...state,
      displayDate: value[0],
    }));
  };

  const handleChangeDisplayTime = (value: string): void => {
    const targetTime = (value as string).split(':').map((time) => parseInt(time, 10));

    setState((state) => ({
      ...state,
      displayDate: set(displayDate, {
        hours: targetTime[0],
        minutes: targetTime[1],
      }),
    }));
  };

  useEffect(() => {
    viewPortRef.current = document.getElementById(VIEWPORT_ID);

    const viewPortWidth = viewPortRef.current?.getAttribute('width') ?? null;

    if (viewPortWidth) {
      setWidth(viewPortWidth);
    }

    return () => {
      viewPortRef.current = null;
    };
  }, []);

  const handleClickMobile = (): void => {
    setIsMobile(true);
    setViewPortWidth('360px');
  };

  const handleClickPC = (): void => {
    setIsMobile(false);
    setViewPortWidth(`${width}px`);
  };

  const setViewPortWidth = (value: string): void => {
    if (viewPortRef.current) {
      viewPortRef.current.style.width = value;
    }
  };

  return (
    <PreviewPage>
      <Header>
        <HeaderButton data-testid="close-button" onClick={handleClickClosePreview}>
          <BsArrowLeft />
        </HeaderButton>
        <DisplayViewMode>
          <HeaderButton data-testid="mobile-button" onClick={handleClickMobile} isActive={isMobile}>
            <BiMobile />
          </HeaderButton>
          <HeaderButton data-testid="pc-button" onClick={handleClickPC} isActive={!isMobile}>
            <HiOutlineDesktopComputer />
          </HeaderButton>
        </DisplayViewMode>
        <DisplayDateArea>
          <FieldItem label={<BiCalendar />} direction="HORIZONTAL">
            <DatePicker
              selected={displayDate}
              dateFormat="yyyy-MM-dd"
              placeholderText="날짜"
              onChange={handleChangeDisplayDate}
            />
          </FieldItem>
          <Select
            data-testid="preview-time"
            value={format(displayDate, `HH:${getMinutes(displayDate).toString().padStart(2, '0')}`)}
            size={32}
            options={TIME_OPTIONS}
            onValue={(value) => handleChangeDisplayTime(value as string)}
            popperOptions={{
              modifiers: [
                {
                  name: 'flip',
                  enabled: false,
                },
                {
                  name: 'hide',
                  enabled: false,
                },
              ],
            }}
          />
        </DisplayDateArea>
      </Header>
      <PageViewport>{ children }</PageViewport>
    </PreviewPage>
  );
}

const PreviewPage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  min-width: 100vw;
  min-height: 100vh;
  overflow: scroll;
  box-sizing: border-box;
  padding: 30px 60px 60px;
  z-index: 10;
  background: ${({ theme }) => theme.base.neutral100};
`;

const Header = styled.div`
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageViewport = styled.div`
  width: 100%;
  box-sizing: border-box;
  min-height: calc(100% - 70px);
  height: max-content;
  background: ${({ theme }) => theme.base.neutral0};
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.05);
`;

const HeaderButton = styled.button<{ isActive?: boolean }>`
  border: none;
  background: none;
  padding: 0;

  > svg {
    width: 36px;
    height: 36px;
    padding: 8px;
    border-radius: 8px;
    color: ${({ theme, isActive }) => isActive ? theme.primary.basic : theme.base.neutral600};
    &:hover {
      cursor: pointer;
      background: ${({ theme }) => theme.base.neutral300};
    }
  }
`;

const DisplayViewMode = styled.div``;

const DisplayDateArea = styled.div`
  display: flex;
  gap: 4px;

  > div {
    width: 170px;
  }
`;
